// Unified State Manager - Single source of truth for app state
// Production Readiness: Centralized state management to prevent inconsistencies
//
// CRASH FIX: Do NOT import aiCoach at top level - creates circular dependency
// (aiCoach -> unifiedStateManager -> aiCoach). Use require() inside methods only.
import { deferredWeakAreaQueue } from './deferredWeakAreaQueue';
import { subscriptionService } from './subscriptionService';
import { masteryService, type MasteryStatus } from './masteryService';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { logger } from '../utils/logger';

/** Local type for test history entries (avoids importing from aiCoach). */
interface TestHistoryEntry {
  testId: string;
  testName: string;
  percentage: number;
  date: string;
}

export interface WeakArea {
  testId: string;
  testName: string;
  score: number;
  detectedAt: string;
  retryCount: number;
  mastered: boolean;
}

export interface AIExamStatus {
  canUnlock: boolean;
  hasCompleted: boolean;
  isPremium: boolean;
  freeUsed: boolean;
  attemptsRemaining: number;
}

export interface FocusAreaState {
  primary: WeakArea | null;
  all: WeakArea[];
  count: number;
}

/**
 * Unified State Manager - Single source of truth
 * 
 * Prevents state inconsistencies by centralizing:
 * - Focus areas (weak areas)
 * - Mastery status
 * - AI Exam status
 * - Test results
 */
class UnifiedStateManager {
  private static instance: UnifiedStateManager;
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 2000; // 2 seconds cache

  /**
   * Get focus areas (weak areas) - single source of truth
   */
  getFocusAreas(): WeakArea[] {
    const cacheKey = 'focusAreas';
    const cached = this.getCached<WeakArea[]>(cacheKey);
    if (cached) return cached;

    const queued = deferredWeakAreaQueue.getQueuedWeakAreas();
    const testHistory = (require('./aiCoach') as { aiCoach: { getTestHistory(): TestHistoryEntry[] } }).aiCoach.getTestHistory();

    // Combine and deduplicate
    const weakAreas = new Map<string, WeakArea>();

    // Add from queue (current weak areas)
    queued.forEach(area => {
      weakAreas.set(area.testId, {
        testId: area.testId,
        testName: area.testName || area.testId,
        score: area.score,
        detectedAt: area.dateDetected || new Date().toISOString(),
        retryCount: area.retryCount || 0,
        mastered: false
      });
    });

    // Add from test history (historical weak areas)
    testHistory.forEach((result: TestHistoryEntry) => {
      if (result.percentage < 60 && !weakAreas.has(result.testId)) {
        // Check if it's actually mastered now
        const masteryStatus = masteryService.getMasteryStatus(result.testId);
        
        if (!masteryStatus.mastered) {
          weakAreas.set(result.testId, {
            testId: result.testId,
            testName: result.testName || result.testId,
            score: result.percentage,
            detectedAt: result.date,
            retryCount: 0,
            mastered: false
          });
        }
      }
    });

    const result = Array.from(weakAreas.values());
    this.setCached(cacheKey, result);
    return result;
  }

  /**
   * Get primary focus area (first weak area)
   */
  getPrimaryFocusArea(): WeakArea | null {
    const focusAreas = this.getFocusAreas();
    return focusAreas.length > 0 ? focusAreas[0] : null;
  }

  /**
   * Get focus area state (for dashboard)
   */
  getFocusAreaState(): FocusAreaState {
    const all = this.getFocusAreas();
    return {
      primary: all.length > 0 ? all[0] : null,
      all,
      count: all.length
    };
  }

  /**
   * Get mastery status for a test - single source of truth
   */
  getMasteryStatus(testId: string): MasteryStatus {
    const cacheKey = `mastery_${testId}`;
    const cached = this.getCached<MasteryStatus>(cacheKey);
    if (cached) return cached;

    const status = masteryService.getMasteryStatus(testId);
    this.setCached(cacheKey, status);
    return status;
  }

  /**
   * Get AI Exam status - single source of truth
   */
  getAIExamStatus(): AIExamStatus {
    const cacheKey = 'aiExamStatus';
    const cached = this.getCached<AIExamStatus>(cacheKey);
    if (cached) return cached;

    const testHistory = (require('./aiCoach') as { aiCoach: { getTestHistory(): TestHistoryEntry[] } }).aiCoach.getTestHistory();
    const { aiExamRegenerationService } = require('./aiExamRegenerationService');
    const canUnlock = aiExamRegenerationService.hasSufficientNewLearning();
    let hasCompleted = testHistory.some((t: TestHistoryEntry) => t.testId === 'ai-exam');
    if (!hasCompleted) {
      try {
        const { getExamHistory } = require('./aiExamUnlockService');
        hasCompleted = getExamHistory().length > 0;
      } catch {
        // ignore
      }
    }
    const isPremium = subscriptionService.hasActiveSubscription();
    const freeUsed = !isPremium && 
                     localStorage.getItem(STORAGE_KEYS.AI_EXAM_FREE_USED) === 'true';

    const status: AIExamStatus = {
      canUnlock,
      hasCompleted,
      isPremium,
      freeUsed,
      attemptsRemaining: isPremium ? Infinity : (freeUsed ? 0 : 1)
    };

    this.setCached(cacheKey, status);
    return status;
  }

  /**
   * Get test results summary
   */
  getTestResultsSummary(): {
    totalTests: number;
    averageScore: number;
    weakAreasCount: number;
    masteredCount: number;
  } {
    const cacheKey = 'testResultsSummary';
    const cached = this.getCached<{
      totalTests: number;
      averageScore: number;
      weakAreasCount: number;
      masteredCount: number;
    }>(cacheKey);
    if (cached) return cached;

    const aiCoach = (require('./aiCoach') as { aiCoach: { getTestHistory(): TestHistoryEntry[]; getTestScores(): Record<string, { average: number; count: number; lastScore: number }> } }).aiCoach;
    const testHistory = aiCoach.getTestHistory();
    const testScores = aiCoach.getTestScores();
    const focusAreas = this.getFocusAreas();

    const totalTests = testHistory.length;
    const scores = testHistory.map((t: TestHistoryEntry) => t.percentage);
    const averageScore = scores.length > 0
      ? scores.reduce((a: number, b: number) => a + b, 0) / scores.length
      : 0;

    // Count mastered tests
    let masteredCount = 0;
    Object.keys(testScores).forEach(testId => {
      const masteryStatus = masteryService.getMasteryStatus(testId);
      if (masteryStatus.mastered) {
        masteredCount++;
      }
    });

    const summary = {
      totalTests,
      averageScore: Math.round(averageScore),
      weakAreasCount: focusAreas.length,
      masteredCount
    };

    this.setCached(cacheKey, summary);
    return summary;
  }

  /**
   * Clear cache (call after state changes)
   */
  clearCache(): void {
    this.cache.clear();
    logger.debug('Unified state cache cleared');
  }

  /**
   * Clear specific cache entry
   */
  clearCacheEntry(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Get cached value
   */
  private getCached<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const age = Date.now() - cached.timestamp;
    if (age > this.CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  /**
   * Set cached value
   */
  private setCached<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Invalidate cache after state changes
   */
  invalidateCache(keys?: string[]): void {
    if (keys) {
      keys.forEach(key => this.cache.delete(key));
    } else {
      this.clearCache();
    }
  }
}

export const unifiedState = new UnifiedStateManager();
